import { Stack, Typography } from "@mui/material";
import { MprcJoinComponents, MprcLink } from "components";
import { Formaters } from "lib/Formaters";
import moment, { Moment } from "moment";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

////////////////////////////////////////////////////////////////////////////

type SupervisorCountersData = {
    classroomCount?: number;
    activeEnrollments: number;
    mentionCount: number;
    executionDueDays: number | null;
    executionDueDate: Date | null;
    executionExpireDaysAfterDueDate: number | null;
    pendingScoreOrAttended: boolean;
    pendingAssScore: boolean;
    registrationAvailableUntil: Date | null;
};

////////////////////////////////////////////////////////////////////////////

export const SupervisorCountersLine = ({ item, onClassroomSelection }: { item: SupervisorCountersData; onClassroomSelection?: () => void; }) => {
    const { t } = useTranslation("lms");
    const currDate = useRef<Date>(new Date());
    const currMomentDate = useRef<Moment>(moment());

    ///
    const buildConclusionOrFinalization = () => {

        if (item.executionDueDate) {
            const diffDays = moment(item.executionDueDate).diff(currMomentDate.current, 'days');
            const expireDate = moment(item.executionDueDate).add((item.executionExpireDaysAfterDueDate ?? 0), 'd').toDate();

            /*
                FINALIZADO <data>
                - Exibido caso data de EXPIRAÇÃO esteja definida e já tenha sido ultrapassada OU data de CONCLUSÃO está definida,
                  foi ultrapassada e não tem nenhuma inscrição ativa [em andamento, não iniciada, aguardando nota]
                - Exibido na cor Error.
            */
            if (item.activeEnrollments == 0 && item.executionDueDate < currDate.current) {
                return <Typography color="error.main" variant="inherit" noWrap sx={{ textTransform: "uppercase" }}>
                    {`${t("Training.Literals.FinalizedAt")} ${Formaters.performaDate(item.executionDueDate, { preposition: "none" })}`}
                </Typography>;
            }

            /*
                CONCLUSÃO EM x DIAS
                - Exibido caso turma tenha data de conclusão em data fixa e faltem menos de 10 dias para a data de conclusão.
            */
            else if (diffDays > 0 && diffDays < 10) {
                return <Typography color="muted.main" variant="inherit" noWrap sx={{ textTransform: "uppercase" }}>
                    {t("Training.Literals.ConclusionAtDays", { count: diffDays })}
                </Typography>;
            }

            /*
                EM ATRASO
                - Exibido caso turma tenha data fixa de CONCLUSÃO, esta data foi ultrapassada E data de EXPIRAÇÃO é nula.
                - Exibir na cor Error.
            */
            else if (item.executionDueDate < currDate.current
                && (item.executionExpireDaysAfterDueDate ?? 0) == 0) {
                return <Typography color="error.main" variant="inherit" noWrap sx={{ textTransform: "uppercase" }}>
                    {t("Training.Literals.Delayed")}
                </Typography>;
            }

            /*
                EM ATRASO - EXPIRA EM x DIAS
                - Exibido caso turma tenha data fixa de CONCLUSÃO, esta data foi ultrapassada E data de EXPIRAÇÃO está definida (não nula)
                  e não foi ultrapassada.
                - Exibir na cor Error.
            */
            else if (item.executionDueDate < currDate.current
                && expireDate > currDate.current
                && item.executionExpireDaysAfterDueDate
                && item.executionExpireDaysAfterDueDate > 0) {
                const diffDays = moment(expireDate).diff(moment(currMomentDate.current), 'days');
                if (diffDays >= 0)
                    return <Typography color="error.main" variant="inherit" noWrap sx={{ textTransform: "uppercase" }}>
                        {`${t("Training.Literals.DelayedExpire")} ${Formaters.dateDiffRelative(expireDate, currDate.current)}`}
                    </Typography>;
            }
        } else if ((item.executionDueDays ?? 0) > 0) {
            /*
                FINALIZADO
                - Se turma tem conclusão em x dias após inscrição, prazo de inscrição está ultrapassado
                  e não existem mais inscrições ativas [em andamento, não iniciada, aguardando nota]
                - Exibido na cor Error.
            */
            if (item.registrationAvailableUntil && item.registrationAvailableUntil < currDate.current && item.activeEnrollments == 0) {
                return <Typography color="error.main" variant="inherit" noWrap sx={{ textTransform: "uppercase" }}>
                    {t("Training.Literals.Finalized")}
                </Typography>;
            }
        }

        return null;
    };

    return (
        <Stack direction="row" flexWrap="wrap">
            <MprcJoinComponents
                components={[
                    item.classroomCount != null && item.classroomCount > 1
                        ? <Typography variant="linkActionHeader" noWrap>
                            <MprcLink onClick={onClassroomSelection}>
                                {t("Monitoring.Supervisor.Literals.ActiveClasses", { count: item.classroomCount })}
                            </MprcLink>
                        </Typography>
                        : null
                    , item.pendingAssScore || item.pendingScoreOrAttended || item.mentionCount > 0
                        ? (
                            <Typography color="warning.main" variant="inherit" noWrap>
                                <span>{t("Monitoring.Supervisor.Literals.Pendings")}:&nbsp;</span>
                                <MprcJoinComponents
                                    components={[
                                        item.mentionCount > 0 ? t("Monitoring.Supervisor.Literals.PendingDiscussions") : null
                                        , item.pendingScoreOrAttended ? t("Monitoring.Supervisor.Literals.PendingScores") : null
                                        , item.pendingAssScore ? t("Monitoring.Supervisor.Literals.PendingAssessment") : null
                                    ]}
                                    separator={<>, </>}
                                />
                            </Typography>
                        )
                        : null
                    , item.activeEnrollments > 0
                        ? <Typography color="muted.main" variant="inherit" noWrap>{t("Monitoring.Supervisor.Literals.ActiveUsers", { count: item.activeEnrollments })}</Typography>
                        : null
                    , item.classroomCount == 1
                        ? buildConclusionOrFinalization()
                        : null
                ]}
                separator={<span>&nbsp;&bull;&nbsp;</span>}
            />
        </Stack>
    );
};
